import axios from "axios";
import { store } from "store";
import { UPDATE_AUTH } from "store/actionTypes";
import { ENDPOINTS } from "./endpoints";
import SHOW_TOAST, { SHOW_INTERNET_TOAST } from "./showToast";
import { FOLDER_TYPE } from "./constants";

export const status_code = {
  success: 200,
  invalid: 400,
  timeout: 408,
  notFound: 204,
  badRequest: 400,
  userDelete: 410,
  serverError: 500,
  Unauthorized: 401,
  successAction: 201,
};

const successCodes = [200, 201, 204];
const sessionExpiryCodes = [401];

/**
 * create axios instance
 */
const $http = axios.create({
  baseURL: `${process.env.REACT_APP_DOMAIN}`,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

const $httpFileUpload = axios.create({
  timeout: 30000,
  baseURL: `${process.env.REACT_APP_DOMAIN}`,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

$http.interceptors.request.use(async (config: any) => {
  if (config.headers) {
    const getState = store?.getState();
    if (getState) {
      const { token = "" } = getState.AuthReducer;
      if (token && token.length > 0) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    }
    return config;
  }
  return config;
});

$httpFileUpload.interceptors.request.use(async (config: any) => {
  if (config.headers) {
    const getState = store?.getState();
    if (getState) {
      const { token = "" } = getState.AuthReducer;
      if (token && token.length > 0) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    }
    return config;
  }
  return config;
});

const sessionExpireHandler = () => {
  store.dispatch({ type: UPDATE_AUTH, payload: { isSessionValid: false } });
};

const getApiErrorMessage = (error: any) => {
  let message =
    error?.response?.data?.message || error?.response?.data?.error;
  if (Array.isArray(message)) {
    message = message[0];
  }
  return message;
};

/**
 * post api call common function
 * @param endPoint endpoint of api
 * @param params data to send
 * @param callback callback function
 */
export const POST_API_CALL = (
  endPoint: string,
  params: object | Array<Object>,
  callback: Function,
) => {
  $http
    .post(endPoint, params)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = getApiErrorMessage(error);

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST(getApiErrorMessage(error));
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

/**
 * post api call common function
 * @param endPoint endpoint of api
 * @param params data to send
 * @param callback callback function
 */
export const POST_API_FILE_UPLOAD_CALL = (
  endPoint: string,
  params: object | Array<Object>,
  callback: Function,
) => {
  $httpFileUpload
    .post(endPoint, params)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = getApiErrorMessage(error);

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST(getApiErrorMessage(error));
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

export const PUT_API_CALL = (
  endPoint: string,
  params: object,
  callback: Function,
) => {
  $http
    .put(endPoint, params)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = getApiErrorMessage(error);

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST(getApiErrorMessage(error));
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

export const GET_API_CALL = (
  endPoint: string,
  params: string,
  callback: Function,
  extraHeaders?: Record<string, string>,
) => {
  $http
    .get(endPoint + params, extraHeaders ? { headers: extraHeaders } : {})
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: Array.isArray(dataToSend)
            ? dataToSend
            : { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = getApiErrorMessage(error);

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST(getApiErrorMessage(error));
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

export const DELETE_API_CALL = (
  endPoint: string,
  callback: Function,
  params?: object,
) => {
  $http
    .delete(endPoint, params ? { data: params } : {})
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = getApiErrorMessage(error);

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST(getApiErrorMessage(error));
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

/**
 * patch api call common function
 * @param endPoint endpoint of api
 * @data data to send
 * @param callback callback function
 */
export const PATCH_API_CALL = (
  endPoint: string,
  data: any,
  callback: Function,
) => {
  $http
    .patch(endPoint, data)
    .then((response: any) => {
      if (
        response &&
        response?.status &&
        successCodes.includes(response?.status)
      ) {
        let dataToSend = response?.data?.data || response?.data;
        callback({
          isSuccess: true,
          data: { ...dataToSend, ...{ message: response?.data?.message } },
        });
      } else {
        SHOW_TOAST(response?.data?.message);
        callback({ isSuccess: false, data: response?.response?.data });
      }
    })
    .catch((error: any) => {
      if (error?.response) {
        //if error contains response
        const statusCode =
          error?.response?.status || error?.response?.data?.status;

        let message = getApiErrorMessage(error);

        if (sessionExpiryCodes.includes(statusCode)) {
          //session expired
          callback({ isSuccess: false, data: {} });
          sessionExpireHandler();
        } else {
          //some other error is there
          SHOW_TOAST(message);
          callback({ isSuccess: false, data: {} });
        }
      } else if (!error.response) {
        //if error does not contain response
        if (error?.code === "ERR_NETWORK") {
          SHOW_INTERNET_TOAST();
        } else {
          SHOW_TOAST(getApiErrorMessage(error));
        }
        callback({ isSuccess: false, data: {} });
      }
    });
};

/**
 * Upload a file to S3 via signed URL.
 * @param file - File to upload
 * @param folder - Folder key from FOLDER in constants.ts
 * @param getSrcUrl - If true, fetch and return the media URL after upload
 */
export const UPLOAD_FILE_TO_S3 = async (
  file: File,
  folder: FOLDER_TYPE,
) => {
  try {
    const data: any = await new Promise((resolve, reject) => {
      POST_API_CALL(
        ENDPOINTS.COMMON.GENERATE_UPLOAD_URL,
        [{ folder, contentType: file.type }],
        (response: any) => {
          if (response.isSuccess) {
            resolve(response.data?.[0] ?? response.data);
          } else {
            reject(new Error("Failed to get upload URL"));
          }
        },
      );
    });
    const uploadData = data?.urls?.[0] || data;

    if (!uploadData?.url || !uploadData?.fields) {
      throw new Error("Invalid upload URL response");
    }

    const { url, fields } = uploadData;
        const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append("file", file);

    await fetch(url, {
      method: "POST",
      body: formData,
      mode: "no-cors",
    });

    if (!fields.key) {
      throw new Error("Missing file key");
    }

        return uploadData?.fileUrl ?? null;
  } catch (error) {
    throw error;
  }
};

const resolveSignedUrlItem = (item: any, fallback = "") => {
  if (!item) return fallback;
  if (typeof item === "string") return item;
  return (
    item.signedUrl ||
    item.readUrl ||
    item.url ||
    item.fileUrl ||
    fallback
  );
};

const mapReadSignedUrls = (data: any, originalUrls: string[]) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.urls)
      ? data.urls
      : Array.isArray(data?.signedUrls)
        ? data.signedUrls
        : Array.isArray(data?.data)
          ? data.data
          : Object.keys(data || {})
              .filter((key) => /^\d+$/.test(key))
              .sort((a, b) => Number(a) - Number(b))
              .map((key) => data[key]);

  return originalUrls.map((original, index) => {
    if (typeof data?.[original] === "string") return data[original];
    const fromList = resolveSignedUrlItem(list?.[index], "");
    if (fromList) return fromList;
    if (index === 0) return resolveSignedUrlItem(data, "");
    return "";
  });
};

/**
 * Convert stored S3 file URLs into browser-readable signed URLs.
 * POST /api/v1/shinology/s3/read/signed-url
 */
export const GET_READ_SIGNED_URLS = async (fileUrls: string[]) => {
  const urls = fileUrls.filter(Boolean);
  if (!urls.length) return [];

  const data: any = await new Promise((resolve, reject) => {
    POST_API_CALL(
      ENDPOINTS.COMMON.GENERATE_READ_SIGNED_URL,
      urls,
      (response: any) => {
        if (response.isSuccess) {
          resolve(response.data);
        } else {
          reject(new Error("Failed to get read signed URL"));
        }
      },
    );
  });

  const signedUrls = mapReadSignedUrls(data, urls);
    return signedUrls;
};

export const extractS3Key = (fileUrlOrKey: string) => {
  if (!fileUrlOrKey) return "";
  try {
    return decodeURIComponent(new URL(fileUrlOrKey).pathname.replace(/^\//, ""));
  } catch {
    return fileUrlOrKey;
  }
};

/**
 * Delete a single file from S3 by file URL or object key.
 * DELETE /api/v1/shinology/s3/delete-file?key=
 */
export const DELETE_S3_FILE = async (fileUrlOrKey: string) => {
  try {
    const key = extractS3Key(fileUrlOrKey);
    if (!key) return null;

        const res = await new Promise((resolve, reject) => {
      DELETE_API_CALL(
        `${ENDPOINTS.COMMON.DELETE_FILE}?key=${encodeURIComponent(key)}`,
        (response: any) => {
          if (response.isSuccess) {
            resolve(response.data);
          } else {
            reject(new Error("Failed to delete S3 file"));
          }
        },
      );
    });

        return res;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete multiple S3 files; failures on individual files are logged and skipped.
 */
export const DELETE_S3_FILES = async (fileUrls: string[] = []) => {
  const uniqueUrls = fileUrls.filter(
    (url, index, list) => Boolean(url) && list.indexOf(url) === index,
  );

  await Promise.all(
    uniqueUrls.map((fileUrl) =>
      DELETE_S3_FILE(fileUrl).catch(() => {}),
    ),
  );
};